import { z } from "zod/v4";
import { paginationSearchSchema } from "#/modules/util.pagination/search.pagination";
import { nuqsCommaArray } from "#/modules/util.pagination/search.nuqs";

const sortOrder = ["ascending", "descending"] as const;

/** Multi-value filter param (comma-separated in the URL) for FilterBar-backed overview filters. */
export const overviewFilterValues = () => nuqsCommaArray;

/** The in/notIn operator param that pairs with `overviewFilterValues`. */
export const overviewFilterOperator = () => z.enum(["in", "notIn"]).default("in").catch("in");

/** The keys every overview search schema shares; what `useOverviewSearch`/`buildOverviewQuery` operate on. */
export interface OverviewSearch {
  searchTerm: string;
  orderBy: string;
  orderDirection: (typeof sortOrder)[number];
  page: number;
  pageSize: number;
}

/**
 * Standard overview search schema (searchTerm + sort + pagination) plus optional page-specific
 * fields. `defaults` is derived from the schema itself so it can never drift from the
 * `stripSearchParams` object — which means every `extra` field must carry a `.default(...)`.
 */
export function createOverviewSearchSchema<
  TExtra extends Record<string, z.ZodType> = Record<never, never>,
>(options: { orderBy: string; orderDirection?: (typeof sortOrder)[number]; extra?: TExtra }) {
  const direction = options.orderDirection ?? "ascending";
  const schema = paginationSearchSchema.extend({
    searchTerm: z.string().default(""),
    orderBy: z.string().default(options.orderBy),
    orderDirection: z.enum(sortOrder).default(direction).catch(direction),
    // SAFETY: with no `extra`, the schema has no extra keys — an empty TExtra.
    ...(options.extra ?? ({} as TExtra)),
  });
  return { schema, defaults: schema.parse({}) };
}
